// src/components/map/hooks/useBaseLayerFilterSync.ts
//
// Posts the base well layer's OWN filter (EVA-35) — independent of the
// well-list filter — and keeps `baseLayerFilterId` in the map store current.
// Tiles (getBaseWellLayerTileUrl) and the resource-play dropdown
// (MapLayerSettings) reference this id; map-svc resolves the viewport, org
// and entitled sources server-side, so the client never sends `?plays=` or
// `?bbox=` again.
//
// Reposts when:
//  - the selected resource plays change (already debounced 300ms by the
//    setter in MapLayerSettings — this hook reacts to the settled value)
//  - the org's survey-source preference changes (order or active state)
//  - the org's header-source preference changes (order or active state) — a
//    separately-settable category from survey (see useHeaderDataSources).
//    eva-map-svc resolves Header.ResourcePlay for this filter's own GroupBy
//    from that order, so a reorder changes the well set behind an unchanged
//    filterId unless this reposts.
//  - the user's org changes (a fresh login or an org switch) — NOT a bare
//    JWT refresh with the same org, which must not repost (see below). An
//    org switch also clears any selected resource plays FIRST: they name the
//    OLD org's plays, which the new org has no wells in, and posting them
//    would filter the base well layer down to zero wells for the new org,
//    silently going blank with no indication anything went wrong (EVA-35 PR
//    #6601 round-4 review). The clear happens as its own state update rather
//    than inline in this repost, so the repost that actually fires always
//    reads the corrected (cleared) selection — see the org-switch branch
//    below.
//  - the server says the current filter has expired or is unknown (410),
//    bounded by a cooldown window (see FILTER_EXPIRY_RETRY_COOLDOWN_MS)
//    rather than "one retry per success": a repost's own 200 does NOT prove
//    the new filterId actually serves tiles — mapbox can still have in-flight
//    requests against the OLD url, which 410 in turn once the repost's
//    `previousFilterId` chaining shortens that old filter's server-side TTL.
//    Resetting the bound on a bare repost success (or on a later tile load)
//    re-arms it for exactly that straggler, and each retry mints a fresh
//    filterId whose OWN stragglers can repeat the cycle — an indefinite
//    alternating chain of reposts and full tile cold-busts on every straggler
//    landing. A time-window cooldown defeats THAT chain regardless of
//    ordering: it doesn't matter whether the next 410 is a genuine new
//    incident or a straggler from the repost that just happened, only how
//    soon it arrives. The cooldown is set (not reset to "clear") on every
//    attempt, success or failure alike, so a repost whose own POST fails
//    can't permanently latch retries off for the rest of the session either
//    — the next 410 after the window elapses gets its own attempt regardless.
//    This is a RATE LIMIT, not a cap, and deliberately so: against a
//    persistently-410ing server it settles into one repost (plus one full
//    tile cold-bust) every FILTER_EXPIRY_RETRY_COOLDOWN_MS for as long as the
//    map stays mounted — unlike the 503 "not ready yet" path next door
//    (baseWellTileErrors.ts's BASE_WELL_MAX_RETRIES), which stops retrying
//    after a fixed count. That asymmetry is intentional: a 410 means the
//    filter this hook itself owns is gone, so retrying is the only way to
//    recover — capping it would leave the layer permanently broken instead.
//
// Deliberately does NOT re-post on map movement — the base layer's filter has
// no viewport predicate; map-svc scopes tiles to the requested {z}/{x}/{y}
// itself.
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";

import { RootState } from "store/rootReducer";
import {
  selectBaseLayerResourcePlays,
  selectSetBaseLayerFilterId,
  selectSetBaseLayerResourcePlays,
  useMapStore
} from "stores/map";

import { USER_DATA_SOURCES_QUERY_KEY, fetchUserDataSources } from "api/dataSource";

import { useHeaderDataSources } from "components/org-settings/hooks/useHeaderDataSources";
import { useSurveyDataSources } from "components/org-settings/hooks/useSurveyDataSources";

import { postBaseLayerFilter } from "../utils/baseLayerFilter";
import { onBaseWellFilterExpired } from "../utils/baseWellTileErrors";
import {
  buildHeaderPreferenceToken,
  buildSurveyPreferenceToken
} from "../utils/surveyPreferenceToken";

/**
 * Minimum time between 410-triggered reposts — a rate limit (one retry per
 * window, indefinitely), not an absolute cap. Long enough to absorb the
 * straggler timeline above — an in-flight request against a URL this hook
 * just superseded resolves on ordinary network latency (at most a few
 * seconds), never anywhere near this window — short enough that a genuinely
 * new, independent expiry occurring later in the same session still gets its
 * own retry promptly. See the module doc comment above for why this stays
 * uncapped rather than adopting the 503 path's fixed retry count.
 */
export const FILTER_EXPIRY_RETRY_COOLDOWN_MS = 30_000;

/**
 * @param enabled Skip this hook entirely when false (e.g. a popout dashboard,
 * which never shows the base layer) — including the org-switch clear of
 * `baseLayerResourcePlays` below.
 * @param postFilter Skip only the posting (initial, reactive, and 410-retry)
 * when false, while still running the org-switch clear — a flagged-off base
 * layer still owns that clear, since it's the only writer of the field.
 */
export function useBaseLayerFilterSync(enabled: boolean, postFilter = true): void {
  const baseLayerResourcePlays = useMapStore(selectBaseLayerResourcePlays);
  const setBaseLayerFilterId = useMapStore(selectSetBaseLayerFilterId);
  const setBaseLayerResourcePlays = useMapStore(selectSetBaseLayerResourcePlays);

  const surveyDataSources = useSurveyDataSources();
  const surveyPreferenceToken = useMemo(
    () => buildSurveyPreferenceToken(surveyDataSources),
    [surveyDataSources]
  );
  const headerDataSources = useHeaderDataSources();
  const headerPreferenceToken = useMemo(
    () => buildHeaderPreferenceToken(headerDataSources),
    [headerDataSources]
  );
  // useSurveyDataSources and useHeaderDataSources both read this exact
  // query (only their `select` differs), and both fall back to `[]` until it
  // resolves — an "every source" placeholder token, distinct from whatever
  // the org's real preference order turns out to be. Posting against that
  // placeholder and then posting AGAIN the moment the real order lands is two
  // org-wide zero-predicate filter posts (a GroupBy call in map-svc apiece)
  // and two full base-tile refetches for what is really one page load.
  // Subscribing to the same key here (no separate `select`) piggybacks on
  // that shared cache entry rather than issuing a third fetch, and its
  // `isLoading` tells us when EITHER preference has actually settled.
  const { isLoading: isPreferenceQueryLoading } = useQuery(
    USER_DATA_SOURCES_QUERY_KEY,
    fetchUserDataSources
  );

  const orgId = useSelector((state: RootState) => state.auth.user?.organization?.id);
  // Last orgId this hook has seen — lets the repost effect below tell an
  // actual org switch apart from the first render (where there is no
  // "previous" org to have stale plays from). Initialized to the mount-time
  // orgId, not undefined, so the very first run is never mistaken for a
  // switch.
  const previousOrgIdRef = useRef<Maybe<string>>(orgId);

  // The last filterId this hook obtained — chained as `previousFilterId` so
  // the server shortens that filter's TTL, and reused (best-effort) as the
  // 410 retry's own previousFilterId.
  const filterIdRef = useRef<Maybe<string>>(undefined);
  // See FILTER_EXPIRY_RETRY_COOLDOWN_MS — timestamp of the last 410-triggered
  // repost attempt, regardless of whether that attempt's POST succeeded.
  const lastExpiryRetryAtRef = useRef<number | undefined>(undefined);

  const repost = useCallback(async () => {
    const nextId = await postBaseLayerFilter(baseLayerResourcePlays, filterIdRef.current);
    // A failed post keeps whatever filterId is already working rather than
    // blanking the layer out.
    if (nextId === undefined) return;
    filterIdRef.current = nextId;
    setBaseLayerFilterId(nextId);
  }, [baseLayerResourcePlays, setBaseLayerFilterId]);

  useEffect(() => {
    if (!enabled || isPreferenceQueryLoading) return;

    const isOrgSwitch =
      previousOrgIdRef.current !== undefined && previousOrgIdRef.current !== orgId;
    previousOrgIdRef.current = orgId;

    if (isOrgSwitch && baseLayerResourcePlays !== undefined) {
      // The selection names the OLD org's plays — clear it rather than
      // reposting with it. Clearing (not reposting here directly) means this
      // effect re-runs on the resulting `baseLayerResourcePlays` change
      // (it's a dependency below) with `isOrgSwitch` now false and the
      // selection already cleared, so the actual repost this org switch
      // triggers never carries a single stale play name.
      setBaseLayerResourcePlays(undefined);
      return;
    }

    if (!postFilter) return;

    void repost();
    // Every dependency here is a distinct repost trigger the design calls
    // for — plays, survey preference, header preference, and org/login.
    // Deliberately excludes anything viewport-related (map movement must not
    // repost), and deliberately excludes the JWT token itself: a token
    // refresh changes this string every ~15-20 minutes with no change to the
    // org or the filter, and the new token is picked up for free (axios
    // attaches it via a global default header set by the auth reducer, not by
    // this hook), so keying on it here would only mint a needless new
    // filterId — busting every visible base tile's cache — on every refresh.
    // `orgId` already fires this effect for the cases that actually matter:
    // initial login and switching orgs, both of which change `orgId` in the
    // same state update that sets the token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    isPreferenceQueryLoading,
    baseLayerResourcePlays,
    surveyPreferenceToken,
    headerPreferenceToken,
    orgId,
    postFilter
  ]);

  useEffect(() => {
    if (!enabled || !postFilter) return undefined;
    return onBaseWellFilterExpired(() => {
      const now = Date.now();
      if (
        lastExpiryRetryAtRef.current !== undefined &&
        now - lastExpiryRetryAtRef.current < FILTER_EXPIRY_RETRY_COOLDOWN_MS
      ) {
        return;
      }
      // Set BEFORE the repost settles, and regardless of whether it
      // eventually succeeds — a failed post must not leave the window stuck
      // open (an immediate, tight retry loop against a failing endpoint) NOR
      // stuck permanently latched past this window (see the module doc
      // comment on the failure case).
      lastExpiryRetryAtRef.current = now;
      void repost();
    });
  }, [enabled, postFilter, repost]);
}
